import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { workplaceQueryKeys } from "./business/useWorkplaceDetails";
import { geolocationKeys } from "./geolocation/useGeolocationSettingsQuery";
import type { SplitShiftConfig, HolidaySyncStatus } from "@/types/companySettings";
import { DEFAULT_SPLIT_SHIFT_CONFIG } from "@/types/companySettings";

// Scheduling-specific company settings (subset of full company_settings table)
interface SchedulingSettings {
  company_id: string;
  enable_offers: boolean;
  enable_swaps: boolean;
  require_approval_offers: boolean;
  require_approval_swaps: boolean;
  approver_roles: string[];
  swap_auto_expire_hours: number | null;
  swap_requires_approval: boolean | null;
  split_shift_config: SplitShiftConfig;
  holidays?: string[]; // ISO date strings (YYYY-MM-DD)
  country_code?: string | null;
  region_code?: string | null;
  holiday_sync_status?: HolidaySyncStatus;
  holiday_auto_sync?: boolean;
  updated_at: string;
}

type CompanySettings = SchedulingSettings;

// Tenant-scoped query keys — a company switch can never serve another
// tenant's cached settings row.
export const companySettingsQueryKeys = {
  all: ['companySettings'] as const,
  details: (companyId?: string) =>
    [...companySettingsQueryKeys.all, 'details', companyId ?? 'none'] as const,
} as const;

/**
 * Refresh bus for callers that live outside React Query (or outside a
 * component), e.g. after the `update_business_hours` RPC which also writes
 * `company_settings.timezone`. Every mounted hook instance listens and
 * invalidates its own query.
 */
type SettingsListener = (companyId: string) => void;
const settingsListeners = new Set<SettingsListener>();

export function requestCompanySettingsRefresh(companyId?: string) {
  settingsListeners.forEach((listener) => {
    try {
      listener(companyId ?? '*');
    } catch {
      // A single bad listener must not break the others
    }
  });
}

// Stable defaults object to prevent re-renders
const DEFAULTS: CompanySettings = {
  company_id: "",
  enable_offers: true,
  enable_swaps: true,
  require_approval_offers: false,
  require_approval_swaps: true,
  approver_roles: ['ADMIN', 'MANAGER', 'SUPERVISOR'],
  swap_auto_expire_hours: null,
  swap_requires_approval: null,
  split_shift_config: DEFAULT_SPLIT_SHIFT_CONFIG,
  updated_at: new Date().toISOString(),
};

export function useCompanySettings() {
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const queryClient = useQueryClient();
  const queryKey = companySettingsQueryKeys.details(companyId);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CompanySettings> => {
      const { data, error } = await supabase.rpc("get_company_settings");
      if (error) throw error;
      return normalize(data);
    },
    enabled: !!companyId,
    staleTime: 30_000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    initialData: () => readCachedSettings(companyId),
    initialDataUpdatedAt: 0,
  });

  const settings: CompanySettings = query.data ?? DEFAULTS;

  // Persist to localStorage for instant UX on next mount / cross-tab sync
  useEffect(() => {
    if (!companyId || !query.data) return;
    try {
      localStorage.setItem(lsKeyFor(companyId), JSON.stringify(query.data));
    } catch {
      // Quota or private mode — cache is best-effort only
    }
  }, [companyId, query.data]);

  const refresh = useCallback(async () => {
    if (!companyId) return;
    await queryClient.invalidateQueries({ queryKey: companySettingsQueryKeys.all });
  }, [companyId, queryClient]);

  const mutation = useMutation({
    mutationFn: async (patch: CompanySettingsPatch) => {
      if (!companyId) throw new Error("No company ID available");
      const { data, error } = await supabase.rpc("set_company_settings", {
        p_enable_offers: patch.enable_offers,
        p_enable_swaps: patch.enable_swaps,
        p_require_approval_offers: patch.require_approval_offers,
        p_require_approval_swaps: patch.require_approval_swaps,
        p_approver_roles: patch.approver_roles,
        p_split_shift_config: patch.split_shift_config ? JSON.parse(JSON.stringify(patch.split_shift_config)) : undefined,
        p_holidays: patch.holidays,
        p_country_code: patch.country_code,
        p_region_code: patch.region_code,
        p_holiday_sync_status: patch.holiday_sync_status ? JSON.parse(JSON.stringify(patch.holiday_sync_status)) : undefined,
        p_holiday_auto_sync: patch.holiday_auto_sync,
      });
      if (error) throw error;
      return data ? normalize(data) : null;
    },
    onMutate: async (patch: CompanySettingsPatch) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CompanySettings>(queryKey);
      queryClient.setQueryData<CompanySettings>(queryKey, {
        ...(previous ?? DEFAULTS),
        ...patch,
      } as CompanySettings);
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onSuccess: (finalSettings) => {
      if (finalSettings) {
        queryClient.setQueryData(queryKey, finalSettings);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
      // `set_company_settings` writes the same row that the workplace and
      // geolocation queries read — keep those caches coherent.
      queryClient.invalidateQueries({ queryKey: workplaceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: geolocationKeys.all });
    },
  });

  const updateSettings = useCallback(
    async (patch: CompanySettingsPatch) => {
      await mutation.mutateAsync(patch);
    },
    [mutation],
  );

  // External refresh requests (writes that bypass this hook)
  useEffect(() => {
    if (!companyId) return;
    const listener: SettingsListener = (eventCompanyId) => {
      if (eventCompanyId !== '*' && eventCompanyId !== companyId) return;
      queryClient.invalidateQueries({ queryKey: companySettingsQueryKeys.all });
    };
    settingsListeners.add(listener);
    return () => {
      settingsListeners.delete(listener);
    };
  }, [companyId, queryClient]);

  // Cross-tab sync: another tab wrote the localStorage mirror
  useEffect(() => {
    if (!companyId) return;
    const lsKey = lsKeyFor(companyId);
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== lsKey) return;
      queryClient.invalidateQueries({ queryKey });
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [companyId, queryClient, queryKey]);

  return {
    settings,
    isLoading: query.isLoading,
    refresh,
    updateSettings,
  };
}

export type CompanySettingsPatch = Partial<Pick<CompanySettings,
  | 'enable_offers'
  | 'enable_swaps'
  | 'require_approval_offers'
  | 'require_approval_swaps'
  | 'approver_roles'
  | 'split_shift_config'
  | 'holidays'
  | 'region_code'
  | 'country_code'
  | 'holiday_sync_status'
  | 'holiday_auto_sync'
>>;

const lsKeyFor = (companyId: string) => `company_settings::${companyId}::v1`;

function normalize(data: any): CompanySettings {
  return {
    ...DEFAULTS,
    ...data,
    split_shift_config:
      (data?.split_shift_config as CompanySettings['split_shift_config']) || DEFAULTS.split_shift_config,
    holidays: (data?.holidays as string[]) || [],
    holiday_sync_status: (data?.holiday_sync_status as HolidaySyncStatus) || undefined,
  } as CompanySettings;
}

function readCachedSettings(companyId?: string): CompanySettings | undefined {
  if (!companyId) return undefined;
  try {
    const cached = localStorage.getItem(lsKeyFor(companyId));
    if (!cached) return undefined;
    return { ...DEFAULTS, ...JSON.parse(cached) } as CompanySettings;
  } catch {
    return undefined;
  }
}

// Centralized feature flag utility functions
export const isOffersEnabled = (settings: CompanySettings | null | undefined): boolean => 
  settings?.enable_offers ?? false;

export const isSwapsEnabled = (settings: CompanySettings | null | undefined): boolean => 
  settings?.enable_swaps ?? true;
